/**
 * useSoundSettings — React hook wrapping the sound settings data layer.
 *
 * For non-React usage (lib functions, callbacks), import from '@/lib/audio/soundSettings' directly.
 */

import { useState, useCallback } from 'react';
import {
  readSoundSettings,
  writeSoundSettings,
  type SoundSettings,
} from '@/lib/audio/soundSettings';

// Re-export for consumers that already import from this file
export { readSoundSettings, type SoundSettings } from '@/lib/audio/soundSettings';

export function useSoundSettings() {
  const [settings, setSettings] = useState<SoundSettings>(readSoundSettings);

  const update = useCallback((patch: Partial<SoundSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      writeSoundSettings(next);
      return next;
    });
  }, []);

  return {
    ...settings,
    setTradeSoundEnabled: (v: boolean) => update({ tradeSoundEnabled: v }),
    setWhaleSoundEnabled: (v: boolean) => update({ whaleSoundEnabled: v }),
    setFirstSaleSoundEnabled: (v: boolean) => update({ firstSaleSoundEnabled: v }),
    setAllTradesSound: (v: boolean) => update({ allTradesSound: v }),
    setDefenseSoundEnabled: (v: boolean) => update({ defenseSoundEnabled: v }),
    setSweepSoundEnabled: (v: boolean) => update({ sweepSoundEnabled: v }),
    setExposureBreachSoundEnabled: (v: boolean) => update({ exposureBreachSoundEnabled: v }),
    setAdverseFlowSoundEnabled: (v: boolean) => update({ adverseFlowSoundEnabled: v }),
    setQueueFrontSoundEnabled: (v: boolean) => update({ queueFrontSoundEnabled: v }),
    setBestPriceSoundEnabled: (v: boolean) => update({ bestPriceSoundEnabled: v }),
    setBootSoundEnabled: (v: boolean) => update({ bootSoundEnabled: v }),
    setVolume: (v: number) => update({ volume: Math.max(0, Math.min(1, v)) }),
    setMinFillContracts: (v: number) => update({ minFillContracts: Math.max(0, Math.floor(v)) }),
  };
}
